


export class ValidationError extends Error {
  field?: string;
  code?: string;

  constructor(
    message: string,
    field?: string,
    code?: string
  ) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.code = code;
  }
}


export function sanitizeHtml(input: string): string {
  if (typeof input !== 'string') return '';

  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
}


export function validateEmail(email: unknown): string {
  if (typeof email !== 'string') {
    throw new ValidationError('Email deve ser uma string', 'email', 'INVALID_TYPE');
  }

  const sanitized = email.trim().toLowerCase();
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

  if (!emailRegex.test(sanitized)) {
    throw new ValidationError('Email inválido', 'email', 'INVALID_FORMAT');
  }

  if (sanitized.length > 254) {
    throw new ValidationError('Email muito longo', 'email', 'TOO_LONG');
  }

  return sanitized;
}


export function validatePassword(password: unknown): string {
  if (typeof password !== 'string') {
    throw new ValidationError('Senha deve ser uma string', 'password', 'INVALID_TYPE');
  }

  if (password.length < 8) {
    throw new ValidationError('Senha deve ter no mínimo 8 caracteres', 'password', 'TOO_SHORT');
  }

  if (password.length > 128) {
    throw new ValidationError('Senha muito longa', 'password', 'TOO_LONG');
  }

  if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    throw new ValidationError(
      'Senha deve conter letras e números',
      'password',
      'WEAK_PASSWORD'
    );
  }

  return password;
}


export function validateText(
  input: unknown,
  fieldName: string,
  options: {
    minLength?: number;
    maxLength?: number;
    required?: boolean;
    allowEmpty?: boolean;
  } = {}
): string {
  const { minLength = 0, maxLength = 1000, required = true, allowEmpty = false } = options;

  if (typeof input !== 'string') {
    throw new ValidationError(
      `${fieldName} deve ser texto`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  const sanitized = sanitizeHtml(input).trim();

  if (required && sanitized.length === 0) {
    throw new ValidationError(
      `${fieldName} é obrigatório`,
      fieldName,
      'REQUIRED'
    );
  }

  if (!allowEmpty && sanitized.length === 0 && input.length > 0) {
    throw new ValidationError(
      `${fieldName} não pode estar vazio`,
      fieldName,
      'EMPTY'
    );
  }

  if (sanitized.length < minLength) {
    throw new ValidationError(
      `${fieldName} deve ter no mínimo ${minLength} caracteres`,
      fieldName,
      'TOO_SHORT'
    );
  }

  if (sanitized.length > maxLength) {
    throw new ValidationError(
      `${fieldName} deve ter no máximo ${maxLength} caracteres`,
      fieldName,
      'TOO_LONG'
    );
  }

  return sanitized;
}


export function validateUuid(id: unknown, fieldName: string = 'id'): string {
  if (typeof id !== 'string') {
    throw new ValidationError(
      `${fieldName} inválido`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(id)) {
    throw new ValidationError(
      `${fieldName} com formato inválido`,
      fieldName,
      'INVALID_FORMAT'
    );
  }

  return id.toLowerCase();
}


export function validateDate(dateString: unknown, fieldName: string = 'date'): string {
  if (typeof dateString !== 'string') {
    throw new ValidationError(
      `${fieldName} deve ser uma data válida`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateRegex.test(dateString)) {
    throw new ValidationError(
      `${fieldName} deve estar no formato AAAA-MM-DD`,
      fieldName,
      'INVALID_FORMAT'
    );
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    throw new ValidationError(
      `${fieldName} inválida`,
      fieldName,
      'INVALID_DATE'
    );
  }

  return dateString;
}


export function validateNumber(
  value: unknown,
  fieldName: string,
  options: {
    min?: number;
    max?: number;
    integer?: boolean;
  } = {}
): number {
  const { min, max, integer = false } = options;

  if (typeof value !== 'number' || isNaN(value)) {
    throw new ValidationError(
      `${fieldName} deve ser um número`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  if (integer && !Number.isInteger(value)) {
    throw new ValidationError(
      `${fieldName} deve ser um número inteiro`,
      fieldName,
      'NOT_INTEGER'
    );
  }

  if (min !== undefined && value < min) {
    throw new ValidationError(
      `${fieldName} deve ser no mínimo ${min}`,
      fieldName,
      'TOO_SMALL'
    );
  }

  if (max !== undefined && value > max) {
    throw new ValidationError(
      `${fieldName} deve ser no máximo ${max}`,
      fieldName,
      'TOO_LARGE'
    );
  }

  return value;
}


export function validateEnum<T extends string>(
  value: unknown,
  allowedValues: readonly T[],
  fieldName: string
): T {
  if (typeof value !== 'string') {
    throw new ValidationError(
      `${fieldName} inválido`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  if (!allowedValues.includes(value as T)) {
    throw new ValidationError(
      `${fieldName} deve ser um dos valores: ${allowedValues.join(', ')}`,
      fieldName,
      'INVALID_VALUE'
    );
  }

  return value as T;
}


export function validateColor(color: unknown, fieldName: string = 'color'): string {
  if (typeof color !== 'string') {
    throw new ValidationError(
      `${fieldName} deve ser uma string`,
      fieldName,
      'INVALID_TYPE'
    );
  }

  const hexRegex = /^#?[0-9A-Fa-f]{6}$/;

  if (!hexRegex.test(color)) {
    throw new ValidationError(
      `${fieldName} deve ser uma cor hexadecimal válida`,
      fieldName,
      'INVALID_FORMAT'
    );
  }

  return color.startsWith('#') ? color : `#${color}`;
}
